import { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSave, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Form } from "react-bootstrap";
import { CHALLENGE_EXPLANATION_LLM_PROMPT, colors, constants, generateRoutePath } from "utils";
import IconInfo from "assets/Icon-Info.svg";
import IconCopySimple from "assets/Icon-CopySimple.svg";
import { ExplanationImage } from "./ExplanationImage";
import { SavePromptModal } from "./SavePromptModal";
import { LoadPromptModal } from "./LoadPromptModal";

const CopyButtonComponent = ({ onClick, text }) => {
  return <Button className="group-code-button" onClick={onClick} style={{ margin: "2px 4px" }}>
    <div style={{alignItems: "center", display: "flex", gap: "4px", justifyContent: "center"}}>
      <img src={IconCopySimple}/>
      {text}
    </div>
  </Button>;
}

export const ExplanationAIPromptConsole = () => {
  const openAITextModels = ["gpt-4.1", "gpt-4.1-mini", "gpt-4.1-nano"];
  const openAIImageModels = ["gpt-image-1", "dall-e-3", "dall-e-2"];
  const googleTextModels = ["gemini-4-argon", "gemini-2.5-pro-preview-05-06", "gemini-2.5-flash-preview-05-20"];
  const googleImageModels = ["imagen-3.0-generate-002", "gemini-2.0-flash-preview-image-generation"];
  const anthropicTextModels = ["claude-sonnet-4-0", "claude-3-7-sonnet-latest"];
  const { authUserData, loading } = useAuthUserData();
  const [formData, setFormData] = useState({});
  const [submittedChallenges, setSubmittedChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [accumulatedErrors, setAccumulatedErrors] = useState([]);
  const [explanations, setExplanations] = useState([]);
  const [progressMessage, setProgressMessage] = useState("");
  const [downloadCsv, setDownloadCsv] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const history = useHistory();
  const promptTextareaRef = useRef(null);

  const adjustPromptHeight = () => {
    if (promptTextareaRef.current) {
      const textarea = promptTextareaRef.current;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const generateCsvContent = () => {
    let csvContent = "challenge_id,challenge,standard_descriptions,llm_provider,llm,prompt,temperature,explanation\n";

    for (let i = 0; i < submittedChallenges.length; i++) {
      const challengeId = submittedChallenges[i];
      const explanation = explanations[i];
      const challenge = JSON.stringify(explanation?.challenge);
      const escapedChallenge = `"${challenge.replace(/"/g, '""')}"`;
      const standardDescriptions = explanation?.standard_descriptions || [];
      const standardDescriptionsOutput = Array.isArray(standardDescriptions) ? JSON.stringify(standardDescriptions) : "";
      const escapedStandardDescriptions = `"${standardDescriptionsOutput.replace(/"/g, '""')}"`;

      // Extract explanation text and escape quotes for CSV
      const explanationArray = explanation?.explanation || [];
      const explanationOutput = Array.isArray(explanationArray) ? JSON.stringify(explanationArray) : "";
      const escapedExplanation = `"${explanationOutput.replace(/"/g, '""')}"`;

      // Escape and wrap prompt in quotes to handle commas and other special characters
      const escapedPrompt = `"${formData.prompt.replace(/"/g, '""')}"`;

      csvContent += `${challengeId},${escapedChallenge},${escapedStandardDescriptions},${formData.text_llm_provider},${formData.llm},${escapedPrompt},${formData.temperature ?? 1},${escapedExplanation}\n`;
    }

    return csvContent;
  };

  const downloadCsvReport = () => {
    if (submittedChallenges.length === 0 || explanations.length === 0) {
      return;
    }

    const csvContent = generateCsvContent();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "explanation_report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    if (downloadCsv && !isLoading && explanations.length > 0 && submittedChallenges.length > 0) {
      downloadCsvReport();
    }
  }, [explanations, downloadCsv, isLoading, submittedChallenges]);

  if (loading) return <></>;
  if (authUserData.isAnonymous) history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));

  const alertStyle = {
    padding: "8px",
    marginBottom: 0,
    marginTop: "16px"
  };

  const handleChange = (e) => {
    if (isLoading) return;
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "prompt") {
      adjustPromptHeight();
    }
  };

  const applyDefaultPrompt = (e) => {
    if (isLoading) return;
    e.preventDefault();
    setFormData((prevState) => ({
      ...prevState,
      prompt: CHALLENGE_EXPLANATION_LLM_PROMPT,
    }));
    setTimeout(() => adjustPromptHeight(), 10);
  };

  const handleGenerate = async (e) => {
    if (isLoading) return;
    e.preventDefault();
    setSubmittedChallenges([]);
    setExplanations([]);
    setIsLoading(true);
    setError(null);
    setAccumulatedErrors([]);
    setProgressMessage("");
    const challenge_ids = formData.challenge_ids.split(",").map(id => id.trim()).filter(id => id);

    // Add delay between requests to avoid rate limiting
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    const requestDelay = 100;
    const maxConcurrentThreads = 20;
    let completedCount = 0;
    let errorCount = 0;
    const results = [];

    // Thread-safe function to add results and update UI
    const addResult = (result) => {
      results.push(result);
      if (result.success) {
        completedCount++;
      } else {
        errorCount++;
      }

      setProgressMessage(`Completed ${completedCount} of ${challenge_ids.length} challenges... (${errorCount} errors, ${maxConcurrentThreads} threads)`);

      // Update UI with current results (sorted by index)
      const sortedResults = results
        .filter(r => r.success)
        .sort((a, b) => a.index - b.index);

      const newSubmittedChallenges = sortedResults.map(r => r.challengeId);
      const newExplanations = sortedResults.map(r => r.response);

      setSubmittedChallenges(newSubmittedChallenges);
      setExplanations(newExplanations);
    };

    const processSingleChallenge = async (challengeId, index) => {
      try {
        const response = await request(`explanation/generate_llm_explanation`, {
          payload: {
            challenge_id: challengeId,
            text_llm_provider: formData.text_llm_provider.toLowerCase(),
            text_model: formData.llm,
            system_prompt: formData.prompt,
            temperature: formData.temperature,
          }
        });

        const result = { challengeId, response, index, success: true };
        addResult(result);
        return result;

      } catch (e) {
        console.log(`Error processing challenge ${challengeId}:`, e);
        logError("Admin explanation console operation failed", e, { category: LogCategory.ERROR });

        // Don't stop on 502 errors, just skip and continue
        if (e?.status === 502 || e?.message?.includes("502")) {
          console.log(`Skipping challenge ${challengeId} due to 502 error`);
        }

        const errorMessage = `Error processing challenge ${challengeId}: ${e?.detail || e?.message || "An unknown error occurred."}`;
        setAccumulatedErrors(prevErrors => [...prevErrors, errorMessage]);

        // Also set the latest error for backward compatibility
        const errors = {};
        if (e.detail && Array.isArray(e.detail)) {
          e.detail.forEach((error) => {
            errors[error.loc.pop()] = error.msg;
          });
        } else {
          errors["generation"] = errorMessage;
        }
        setError(errors);

        const result = { challengeId, error: e, index, success: false };
        addResult(result);
        return result;
      }
    };

    try {
      // Create a pool of worker promises that continuously process challenges
      let challengeIndex = 0;
      const workers = [];

      // Worker function that processes challenges continuously
      const worker = async (workerId) => {
        // Initial stagger to avoid all threads starting simultaneously
        await delay(workerId * 50);

        while (challengeIndex < challenge_ids.length) {
          const currentIndex = challengeIndex++;
          if (currentIndex >= challenge_ids.length) break;

          const challengeId = challenge_ids[currentIndex];
          await processSingleChallenge(challengeId, currentIndex);

          // Small delay between requests from the same worker
          await delay(requestDelay);
        }
      };

      // Start all worker threads
      for (let i = 0; i < maxConcurrentThreads; i++) {
        workers.push(worker(i));
      }

      // Wait for all workers to complete
      await Promise.all(workers);

    } finally {
      setIsLoading(false);
      setProgressMessage(`Completed! ${completedCount} successful, ${errorCount} errors.`);
    }
  };

  const handleCopyToClipboard = (e) => {
    navigator.clipboard.writeText(e.target.innerText)
      .then(() => {});
  };

  const handleSavePrompt = () => {
    setShowSaveModal(true);
  };

  const handleLoadPrompt = () => {
    setShowLoadModal(true);
  };

  const handleClearLoadedPrompt = () => {
    setFormData({
      ...formData,
      loadedPromptId: null,
      loadedPromptName: null
    });
  };

  const handlePromptLoaded = (promptData) => {
    setFormData({
      ...formData,
      prompt: promptData.prompt,
      text_llm_provider: promptData.llm_provider,
      llm: promptData.llm_model,
      temperature: promptData.temperature,
      loadedPromptId: promptData.id,
      loadedPromptName: promptData.name
    });
    setTimeout(() => adjustPromptHeight(), 10);
    setShowLoadModal(false);
  };

  const handlePromptSaved = (savedPromptData) => {
    // Update the current form data with the saved prompt's ID and name
    const updatedFormData = {
      ...formData,
      loadedPromptId: savedPromptData.id,
      loadedPromptName: savedPromptData.name
    };
    setFormData(updatedFormData);
    setShowSaveModal(false);
  };

  return (
    <div style={{
      display: "flex",
      backgroundColor: colors.milkGlass200,
      flexDirection: "column",
      gap: "24px" }}>
      <div style={{
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)" }}>
        <div>
          <Alert style={{ marginBottom: 0 }}
                 variant="">
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{
                  alignItems: "center",
                  display: "flex",
                  minHeight: "36px",
                  gap: "12px",
                  flex: "1 0 0",
                  paddingRight: "28px" }}>
                  <img src={IconInfo} alt="info" />
                  {constants.EXPLANATION_PROMPT_CONSOLE_MESSAGE}
                </div>
              </div>
              <span>Supported LLM providers (case-agnostic):&nbsp;
                <a href="https://platform.openai.com/docs/models#moderation" target="_blank">OpenAI</a>,&nbsp;
                <a href="https://ai.google.dev/gemini-api/docs/models#text-variations" target="_blank">Google</a>&nbsp;and&nbsp;
                <a href="https://docs.anthropic.com/en/docs/about-claude/models/overview" target="_blank">Anthropic.</a>
                &nbsp;Click on provider for the complete list of models.
              </span>
              <span>Best models as of 2025-06 (case-sensitive):</span>
              <span>&nbsp;&nbsp;&nbsp;<span style={{ fontWeight: "bold" }}>OpenAI text models:</span>
                {openAITextModels.map((model, index) => (
                  <CopyButtonComponent key={index} onClick={handleCopyToClipboard} text={model}/>
                ))}
              </span>
              <span>&nbsp;&nbsp;&nbsp;<span style={{ fontWeight: "bold" }}>OpenAI image models:</span>
              {openAIImageModels.map((model, index) => (
                <CopyButtonComponent key={index} onClick={handleCopyToClipboard} text={model}/>
              ))}
              </span>
              <span>&nbsp;&nbsp;&nbsp;<span style={{ fontWeight: "bold" }}>Google text models:</span>
                {googleTextModels.map((model, index) => (
                  <CopyButtonComponent key={index} onClick={handleCopyToClipboard} text={model}/>
                ))}
              </span>
              <span>&nbsp;&nbsp;&nbsp;<span style={{ fontWeight: "bold" }}>Google image models:</span>
                {googleImageModels.map((model, index) => (
                  <CopyButtonComponent key={index} onClick={handleCopyToClipboard} text={model}/>
                ))}
              </span>
              <span>&nbsp;&nbsp;&nbsp;<span style={{ fontWeight: "bold" }}>Anthropic text models:</span>
                {anthropicTextModels.map((model, index) => (
                  <CopyButtonComponent key={index} onClick={handleCopyToClipboard} text={model}/>
                ))}
              </span>
            </div>
          </Alert>
        </div>
      </div>
      <div style={{
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
        padding: "32px" }}>
        <Form onSubmit={handleGenerate}>
          <div style={{ display: "flex", flexDirection: "column" , gap: "32px" }}>
            <div style={{ display: "flex", flexDirection: "column" , gap: "24px" }}>
              <Form.Group>
                <Form.Label style={{ fontWeight: "bold" }}>Challenge Ids</Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Enter comma-separated challenge ids"
                  value={formData.challenge_ids}
                  name="challenge_ids"
                  onChange={handleChange}
                />
                {error?.challenge_ids && (
                  <Alert style={alertStyle} variant="danger">{error.challenge_ids}</Alert>
                )}
              </Form.Group>
              <Form.Group>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <Form.Label style={{ fontWeight: "bold", margin: 0 }}>
                    Prompt&nbsp;<a href="#" onClick={applyDefaultPrompt}>(Click here to apply default prompt)</a>
                    {formData.loadedPromptName && (
                      <span style={{ color: "#28a745", fontSize: "14px", marginLeft: "8px" }}>
                        (Loaded: {formData.loadedPromptName})
                        <button
                          type="button"
                          onClick={handleClearLoadedPrompt}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginLeft: "4px",
                            padding: "0"
                          }}
                          title="Clear loaded prompt"
                        >
                          ✕
                        </button>
                      </span>
                    )}
                  </Form.Label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleSavePrompt}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faSave} className="mr-1" />
                      Save Prompt
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleLoadPrompt}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faFolderOpen} className="mr-1" />
                      Load Prompt
                    </Button>
                  </div>
                </div>
                <Form.Control
                  ref={promptTextareaRef}
                  required
                  as="textarea"
                  placeholder="Enter LLM prompt"
                  value={formData.prompt}
                  name="prompt"
                  onChange={handleChange}
                  style={{ overflow: "hidden" }}
                />
                {error?.prompt && (
                  <Alert style={alertStyle} variant="danger">{error.prompt}</Alert>
                )}
              </Form.Group>
              <div style={{ display: "flex", gap: "32px" }}>
                <Form.Group style={{ width: "100%" }}>
                  <Form.Label style={{ fontWeight: "bold" }}>LLM Provider</Form.Label>
                  <Form.Control
                    required
                    as="select"
                    value={formData.text_llm_provider}
                    name="text_llm_provider"
                    onChange={handleChange}
                  >
                    <option value="">Select LLM provider</option>
                    <option value="openai">OpenAI</option>
                    <option value="google">Google</option>
                    <option value="anthropic">Anthropic</option>
                  </Form.Control>
                  {error?.text_llm_provider && (
                    <Alert style={alertStyle} variant="danger">{error.text_llm_provider}</Alert>
                  )}
                </Form.Group>
                <Form.Group style={{ width: "100%" }}>
                  <Form.Label style={{ fontWeight: "bold" }}>LLM Name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="gpt-4.1, gemini-2.5-pro-preview-05-06, etc."
                    value={formData.llm}
                    name="llm"
                    onChange={handleChange}
                  />
                  {error?.llm && (
                    <Alert style={alertStyle} variant="danger">{error.llm}</Alert>
                  )}
                </Form.Group>
                <Form.Group style={{ width: "100%" }}>
                  <Form.Label style={{ fontWeight: "bold" }}>LLM Warmth (optional, default is 0)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="1.5"
                    step="0.01"
                    placeholder="Enter LLM temperature between 0 and 1.5"
                    value={formData.temperature}
                    name="temperature"
                    onChange={handleChange}
                  />
                  {error?.temperature && (
                    <Alert style={alertStyle} variant="danger">{error.temperature}</Alert>
                  )}
                </Form.Group>
              </div>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="download-csv-checkbox"
                  label="Download CSV report once generation is complete"
                  checked={downloadCsv}
                  onChange={(e) => setDownloadCsv(e.target.checked)}
                  style={{ fontWeight: "bold" }}
                />
              </Form.Group>
              {error?.generation && (
                <Alert style={alertStyle} variant="danger">{error.generation}</Alert>
              )}
              {accumulatedErrors.length > 0 && (
                <Alert style={alertStyle} variant="warning">
                  <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                    Accumulated Errors ({accumulatedErrors.length}):
                  </div>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {accumulatedErrors.map((errorMsg, index) => (
                      <div key={index} style={{ marginBottom: "4px", fontSize: "14px" }}>
                        {index + 1}. {errorMsg}
                      </div>
                    ))}
                  </div>
                </Alert>
              )}
              {progressMessage && (
                <Alert style={alertStyle} variant="info">{progressMessage}</Alert>
              )}
            </div>
            <Button style={{
              backgroundColor: colors.nightlyWoods500,
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              height: "40px",
              padding: "0 16px" }}
                    disabled={isLoading}
                    type="submit">
              Generate explanation
            </Button>
          </div>
        </Form>
      </div>
      {isLoading && <div style={{ alignSelf: "center", position: "fixed", top: "50%" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
      {explanations.map((explanation, index) => (
        <div key={index} style={{
          alignItems: "center",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
          padding: "32px",
          whiteSpace: "pre-wrap" }}>
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <CopyButtonComponent onClick={() => navigator.clipboard.writeText(JSON.stringify(explanation, ["explanation"], 2))
              .then(() => {})} text="Copy explanation to clipboard"/>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <span style={{ fontWeight: "bold" }}>Challenge ID: {submittedChallenges[index]}</span><br />
          </div>
          {JSON.stringify(explanation, ["explanation"], 2)}
          <hr />
          <ExplanationImage imagePrompt={explanation.image_prompt ?? ""} isParentLoading={isLoading}/>
        </div>
      ))}

      <SavePromptModal
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        onPromptSaved={handlePromptSaved}
        currentPromptData={{
          prompt: formData.prompt,
          llm_provider: formData.text_llm_provider,
          llm_model: formData.llm,
          temperature: formData.temperature,
          id: formData.loadedPromptId,
          name: formData.loadedPromptName
        }}
      />

      <LoadPromptModal
        show={showLoadModal}
        onHide={() => setShowLoadModal(false)}
        onPromptLoaded={handlePromptLoaded}
      />
    </div>
  );
};
